/* eslint-disable */
import React, { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SessionContext } from '../../context/sessionContext';
import { useMyVW } from '../../context/myVWContext';
import { useMyVWQueryParams } from '../hooks/useMyVWQueryParams';
import i18n from '../../localization/i18n';
import * as rk from '../../localization/resourceKeys';
import BannerNotification from '../components/common/banner/bannerNotification';
import Loader from '../components/common/loader/loader';
import FormField from '../components/common/form-field/formField';
import { StepLayout } from '../components/common/steplayout/StepLayout';
import SetupAuthenticatorApp from '../components/myVW/MFA/SetupAuthenticatorApp';
import Logger from '../../logger';
import { isValidAccountNumber, restrictKeyPressHandler } from '../../utilities';
import { verifyUpdateEmailIdentity, generateAuthenticatorSecret } from '../services/myVW';

type PageStep = 'verify' | 'setup';

const MyVWUpdateAccountAuthenticator: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const loginAuthenticatorCode = urlParams.get('loginAuthenticatorCode') ?? '';
  const { sessionDetails } = useContext(SessionContext)!;
  const { vwfsToken, partyId, applyAuthState } = useMyVW();
  const { token, brand, country, buildSearch } = useMyVWQueryParams();
  const myVWSessionDetails = { ...sessionDetails, brand, country };
  const sessionToken = vwfsToken || token;
  const restrictNumericKeyPress = restrictKeyPressHandler(/^[0-9]*$/i);

  const [step, setStep] = useState<PageStep>('verify');
  const [usernameInput, setUsernameInput] = useState('');
  const [accountNumberInput, setAccountNumberInput] = useState('');
  const [ssnInput, setSsnInput] = useState('');
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [accountNumberError, setAccountNumberError] = useState<string | null>(null);
  const [ssnError, setSsnError] = useState<string | null>(null);
  const [verifyInvalidError, setVerifyInvalidError] = useState<string | null>(null);
  const [verifyApiError, setVerifyApiError] = useState(false);
  const [verifyAttempts, setVerifyAttempts] = useState(0);
  const [verifyLocked, setVerifyLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [secretURI, setSecretURI] = useState('');
  const [secret, setSecret] = useState('');

  const validateVerify = (): boolean => {
    let valid = true;
    setUsernameError(null);
    setAccountNumberError(null);
    setSsnError(null);

    if (!usernameInput.trim()) {
      setUsernameError(i18n.t(rk.ERROR_API_INVALID_USERNAME));
      valid = false;
    }

    if (!isValidAccountNumber(accountNumberInput)) {
      setAccountNumberError(i18n.t(rk.ERROR_INVALID_ACCNUMBER));
      valid = false;
    }

    if (!/^\d{4}$/.test(ssnInput)) {
      setSsnError(i18n.t(rk.ERROR_SSN_FORMAT));
      valid = false;
    }

    return valid;
  };

  const handleVerifyAndGenerateSecret = async () => {
    if (verifyLocked || !validateVerify()) return;

    setVerifyInvalidError(null);
    setVerifyApiError(false);
    setLoading(true);

    const verifyResult = await verifyUpdateEmailIdentity(
      usernameInput,
      accountNumberInput,
      ssnInput,
      myVWSessionDetails,
    );

    if (!verifyResult.isSuccess) {
      setLoading(false);
      if (verifyResult.isLocked || verifyResult.loginAttemptCount) {
        const count = verifyResult.loginAttemptCount ?? 5;
        setVerifyAttempts(count);
        if (count >= 5 || verifyResult.isLocked) {
          setVerifyLocked(true);
          setVerifyInvalidError(null);
        } else {
          setVerifyInvalidError(i18n.t(rk.ERROR_INVALID_INFO));
        }
      } else {
        setVerifyApiError(true);
      }
      Logger.info('myVW authenticator reset verify failed', 'Location ==> /myVW/update-account-authenticator', sessionDetails);
      return;
    }

    const resolvedPartyId = verifyResult.partyId ?? partyId ?? undefined;
    if (!resolvedPartyId) {
      setLoading(false);
      setVerifyApiError(true);
      return;
    }

    applyAuthState({
      partyId: resolvedPartyId,
      accountNumber: accountNumberInput,
      ssn: ssnInput,
    });

    const generateResult = await generateAuthenticatorSecret(
      sessionToken,
      resolvedPartyId,
      myVWSessionDetails,
      loginAuthenticatorCode || undefined,
    );
    setLoading(false);

    if (!generateResult.isSuccess || !generateResult.secret || !generateResult.secretURI) {
      setVerifyApiError(true);
      Logger.info('myVW authenticator reset generate secret failed', 'Location ==> /myVW/update-account-authenticator', sessionDetails);
      return;
    }

    setSecret(generateResult.secret);
    setSecretURI(generateResult.secretURI);
    Logger.info('myVW authenticator reset secret generated', 'Location ==> /myVW/update-account-authenticator', sessionDetails);
    setStep('setup');
  };

  if (step === 'setup') {
    return (
      <SetupAuthenticatorApp
        onChangeMethod={() => navigate('/myVW/mfa' + buildSearch({ loginAuthenticatorCode }), { replace: true })}
        secretURI={secretURI}
        secret={secret}
      />
    );
  }

  return (
    <StepLayout
      title={<h3 className='u-mb-xsmall'>{i18n.t(rk.TITLE_CONFIRM_IDENTITY)}</h3>}
      primaryAction={{
        label: i18n.t(rk.BUTTON_CANCEL),
        onClick: () => navigate('/myVW/mfa' + buildSearch({ loginAuthenticatorCode }), { replace: true }),
        testId: 'update-account-authenticator-verify-cancel',
      }}
      secondaryAction={{
        label: i18n.t(rk.BUTTON_CONTINUE),
        onClick: handleVerifyAndGenerateSecret,
        disabled: loading || verifyLocked || !usernameInput || !accountNumberInput || !ssnInput,
        testId: 'update-account-authenticator-verify-continue',
      }}
    >
      <Loader isLoading={loading} />
      {verifyLocked && (
        <div className='u-mt' data-testid='update-account-authenticator-verify-lockout-banner'>
          <BannerNotification title={`${i18n.t(rk.TITLE_LOCKOUT_ERROR)}`} notificationStyle='error'>
            {i18n.t(rk.TEXT_LOCKOUT_ERROR_MFA) as any}
          </BannerNotification>
        </div>
      )}
      {verifyAttempts >= 2 && verifyAttempts < 5 && (
        <div className='u-mt' data-testid='update-account-authenticator-verify-warning-banner'>
          <BannerNotification
            title={`${i18n.t(rk.TITLE_LOCKOUT_WARNING1)} ${5 - verifyAttempts}
             ${verifyAttempts === 4 ? i18n.t(rk.TITLE_LOCKOUT_WARNING3) : i18n.t(rk.TITLE_LOCKOUT_WARNING2)}`}
            notificationStyle='warning'
          >
            {i18n.t(rk.TEXT_LOCKOUT_WARNING_VERIFY) as any}
          </BannerNotification>
        </div>
      )}
      {verifyApiError && (
        <div className='u-mt' data-testid='update-account-authenticator-verify-api-error'>
          <BannerNotification notificationStyle='error'>
            {i18n.t(rk.ERROR_API_SERVICE_DOWN) as any}
          </BannerNotification>
        </div>
      )}

      <p className='u-mt'>{i18n.t(rk.TEXT_ALL_FIELDS_REQUIRED)}</p>
      <FormField
        id='update-account-authenticator-verify-username'
        label={i18n.t(rk.LABEL_USERNAME)}
        type='text'
        testid='update-account-authenticator-verify-username-input'
        value={usernameInput}
        disabled={verifyLocked}
        onChange={(e) => {
          setUsernameInput(e.target.value);
          setUsernameError(null);
          setVerifyInvalidError(null);
        }}
        fieldErrors={(usernameError || undefined) as unknown as string[] | undefined}
      />
      <FormField
        id='update-account-authenticator-verify-ssn'
        label={i18n.t(rk.LABEL_SSN_LAST4)}
        type='text'
        testid='update-account-authenticator-verify-ssn-input'
        value={ssnInput}
        disabled={verifyLocked}
        onKeyPress={restrictNumericKeyPress}
        onChange={(e) => {
          if (e.target.value.length > 4) return;
          setSsnInput(e.target.value);
          setSsnError(null);
          setVerifyInvalidError(null);
        }}
        fieldErrors={(ssnError || undefined) as unknown as string[] | undefined}
        hint={i18n.t(rk.TEXT_SSN_LAST4_HINT)}
      />
      <FormField
        id='update-account-authenticator-verify-account-number'
        label={i18n.t(rk.LABEL_ACCOUNT_NUMBER)}
        type='text'
        testid='update-account-authenticator-verify-account-number-input'
        value={accountNumberInput}
        disabled={verifyLocked}
        onKeyPress={restrictNumericKeyPress}
        onChange={(e) => {
          if (e.target.value.length > 10) return;
          setAccountNumberInput(e.target.value);
          setAccountNumberError(null);
          setVerifyInvalidError(null);
        }}
        fieldErrors={(accountNumberError || verifyInvalidError || undefined) as unknown as string[] | undefined}
        hint={i18n.t(rk.TEXT_ACCOUNT_NUM_HINT)}
      />
    </StepLayout>
  );
};

export default MyVWUpdateAccountAuthenticator;
